/**
 * MultiStateEnforcementCalendar — timeline lanes per known real-property state.
 *
 * Consumes POST /api/judgment-law/timeline-multi. Renders one lane per state,
 * sorted by soonest most-urgent deadline. Includes a "Export as PDF" CTA that
 * downloads /api/debtors/:id/multi-state-calendar/pdf.
 */
import React, { useEffect, useState } from "react";
import { MapPin, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/apiClient";
import JeaButton from "@/components/jea/JeaButton";
import JeaCard from "@/components/jea/JeaCard";
import { cn } from "@/lib/utils";

const parseISO = (iso) => (iso ? new Date(iso).getTime() : null);
const shortDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const severityBg = {
  emergency: "border-danger bg-danger/15 text-danger",
  critical: "border-danger bg-danger/10 text-danger",
  urgent: "border-warning bg-warning/10 text-warning",
  recommended: "border-accent-pink bg-accent-pink/10 text-accent-pink",
  info: "border-border-default bg-bg-tertiary text-text-secondary",
};

const StateLane = ({ entry }) => {
  const dates = entry.dates || {};
  const min = Date.now();
  const jExp = parseISO(dates.judgment_expires?.iso);
  const lExp = parseISO(dates.lien_expires?.iso);
  const max = Math.max(jExp || 0, lExp || 0, min + 86400000 * 30);
  const span = max - min || 86400000;

  const xPercent = (ms) => Math.min(100, Math.max(0, ((ms - min) / span) * 100));

  const mu = entry.most_urgent || {};
  const upcoming = [
    dates.renewal_deadline?.iso && {
      label: "Renewal",
      iso: dates.renewal_deadline.iso,
      d: dates.renewal_deadline.days_until,
    },
    dates.judgment_expires?.iso && {
      label: "Expires",
      iso: dates.judgment_expires.iso,
      d: dates.judgment_expires.days_until,
    },
    dates.lien_expires?.iso && {
      label: "Lien",
      iso: dates.lien_expires.iso,
      d: dates.lien_expires.days_until,
    },
  ]
    .filter(Boolean)
    .filter((x) => x.d != null && x.d >= 0)
    .sort((a, b) => a.d - b.d)
    .slice(0, 2);

  return (
    <div
      className="rounded-[10px] bg-bg-tertiary/60 border border-border-default p-4"
      data-testid={`multi-state-lane-${entry.state_key}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-white font-bold text-[15px] jea-mono">{entry.state_key}</div>
          <div className="text-[12px] text-text-secondary">{entry.name}</div>
        </div>
        {mu && mu.severity && (
          <div
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em]",
              severityBg[mu.severity] || severityBg.info,
            )}
            data-testid={`multi-state-severity-${entry.state_key}`}
          >
            <AlertTriangle size={10} />
            {mu.severity} · {mu.days_until}d
          </div>
        )}
      </div>

      <div className="relative h-6 bg-bg-primary rounded-full overflow-visible">
        {/* TODAY marker */}
        <div className="absolute top-0 bottom-0 border-l-2 border-accent-pink" style={{ left: "0%" }} />
        {/* Judgment expires */}
        {jExp && (
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-danger border-2 border-bg-primary"
            style={{ left: `${xPercent(jExp)}%` }}
            title={`Judgment expires ${shortDate(dates.judgment_expires.iso)}`}
          />
        )}
        {/* Lien expires */}
        {lExp && (
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-warning border-2 border-bg-primary"
            style={{ left: `${xPercent(lExp)}%` }}
            title={`Lien expires ${shortDate(dates.lien_expires.iso)}`}
          />
        )}
      </div>

      {upcoming.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          {upcoming.map((u, i) => (
            <div key={i} className="text-[11px]">
              <div className="text-text-secondary">{u.label}</div>
              <div className="text-white font-semibold jea-mono">{shortDate(u.iso)}</div>
              <div className="text-text-secondary jea-mono">{u.d} days</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MultiStateEnforcementCalendar = ({
  debtorId,
  payload,           // { state, date_entered, court_level_variant, lien_filed, lien_filed_at, known_real_property_states }
  testid = "multi-state-calendar",
}) => {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const knownStates = payload?.known_real_property_states || [];
  const jurisdictional = payload?.state;
  // Union: home state + all known real-property states.
  const allCodes = Array.from(new Set([jurisdictional, ...knownStates].filter(Boolean)));

  useEffect(() => {
    if (!payload?.date_entered || allCodes.length < 2) {
      setLoading(false);
      setEntries([]);
      return;
    }
    setLoading(true);
    apiClient
      .post("/judgment-law/timeline-multi", {
        base: {
          state: payload.state,
          date_entered: payload.date_entered,
          court_level_variant: payload.court_level_variant,
          lien_filed: !!payload.lien_filed,
          lien_filed_at: payload.lien_filed_at,
        },
        state_keys: allCodes,
      })
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload?.state, payload?.date_entered, allCodes.join(",")]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get(`/debtors/${debtorId}/multi-state-calendar/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "multi-state-calendar.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  if (allCodes.length < 2) return null;
  if (loading) {
    return (
      <JeaCard className="min-h-[220px] animate-pulse" data-testid={`${testid}-loading`}>
        <div className="jea-caption">Multi-State Enforcement Calendar</div>
      </JeaCard>
    );
  }
  if (!entries?.length) return null;

  return (
    <JeaCard data-testid={testid}>
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-accent-pink" />
          <div className="text-white font-semibold text-[15px]">Multi-State Enforcement Calendar</div>
          <span className="inline-flex px-2 py-0.5 rounded-full bg-accent-pink/15 text-accent-pink text-[11px] font-bold">
            {entries.length}
          </span>
        </div>
        <JeaButton
          variant="outline-pink"
          onClick={exportPdf}
          loading={exporting}
          className="gap-2"
          data-testid={`${testid}-export`}
        >
          <Download size={14} /> Export as PDF
        </JeaButton>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {entries.map((entry) => (
          <StateLane key={entry.state_key} entry={entry} />
        ))}
      </div>
      <div className="mt-4 text-[11px] text-text-muted italic">
        Sorted by soonest most-urgent deadline. Filing procedures and lien durations vary by state — this is a starting checklist, not legal advice.
      </div>
    </JeaCard>
  );
};

export default MultiStateEnforcementCalendar;
